import { useForm } from "@mantine/form";
import { IFocusableForm } from "@type";
import { useMemo } from "react";
import { AutoFocusableForm } from "../type/AutoFocusableForm";
import { FormErrors, UseFormInput } from "@mantine/form/lib/types";
import { Schema, ValidationError } from "yup";

export interface IFocusableFormProps<
  Values = Record<string, unknown>,
  TContext = any,
  TDefault = any,
> extends Partial<UseFormInput<Partial<Values>>> {
  schema: Schema<Values, TContext, TDefault>;
  context?: TContext;
}

const initialValues = {};

export function yupResolver<TType = any, TContext = any, TDefault = any>(
  schema: Schema<TType, TContext, TDefault>,
  context?: TContext,
) {
  const _schema: Schema = schema;

  return (values: Record<string, any>): FormErrors => {
    try {
      _schema.validateSync(values, { abortEarly: false, context });
      return {};
    } catch (_yupError: any) {
      const yupError: ValidationError = _yupError;
      const results = {} as { [key: string]: string };

      yupError.inner.forEach((error) => {
        if (error.path) {
          results[error.path.replaceAll("[", ".").replaceAll("]", "")] =
            error.message;
        }
      });

      return results;
    }
  };
}

export const useFocusableForm = <
  Values = Record<string, unknown>,
  TContext = any,
  TDefault = any,
>({
  schema,
  context,
  ...rest
}: IFocusableFormProps<Partial<Values>, TContext, TDefault>): IFocusableForm<
  Partial<Values>
> => {
  const yupSchema = useMemo(
    () => yupResolver(schema, context),
    [schema, context],
  );

  const form = useForm<Partial<Values>>({
    initialValues: initialValues,
    validateInputOnBlur: true,
    clearInputErrorOnChange: false,
    validate: yupSchema,
    ...rest,
  });
  return useMemo(() => new AutoFocusableForm(form), [form]);
};
